/**
 * Set Command Patterns (Consolidated)
 *
 * Hand-crafted patterns for "set" command across languages.
 * Languages without hand-crafted patterns rely on auto-generation from profiles.
 *
 * Phase 3.2: Consolidated from 19 files into single file.
 */

import type { ExpectedType, LanguagePattern } from '../types';
import { setSchema } from '../generators/command-schemas';

/**
 * A handcrafted set's value takes what the schema's does, an element or an
 * array included (`set el to #panel`, `set x to [1, 2]`), and a possessive
 * (`set x to #d1's value`, which he renders `value מ #d1`). de/fr/id/ms/pt's
 * patterns, and he/sw's, once copied the narrower list, and such a set was
 * lost. (es/qu/zh keep their copies: where they reject a value, the generated
 * patterns read it, so the copies never decide.)
 */
const SET_PATIENT_TYPES: ExpectedType[] = [
  ...(setSchema.roles.find(role => role.role === 'patient')?.expectedTypes ?? []),
];

function getSetPatternsBn(): LanguagePattern[] {
  return [
    // Full pattern: :x কে 5 এ সেট করুন
    {
      id: 'set-bn-full',
      language: 'bn',
      command: 'set',
      priority: 100,
      template: {
        format: '{destination} কে {patient} তে সেট',
        tokens: [
          { type: 'role', role: 'destination' },
          { type: 'literal', value: 'কে' },
          { type: 'role', role: 'patient' },
          { type: 'literal', value: 'তে', alternatives: ['এ'] },
          { type: 'literal', value: 'সেট', alternatives: ['নির্ধারণ'] },
          { type: 'group', optional: true, tokens: [{ type: 'literal', value: 'করুন' }] },
        ],
      },
      extraction: {
        destination: { position: 0 },
        patient: { marker: 'তে', position: 2 },
      },
    },
    // Simple pattern: সেট :x 5
    {
      id: 'set-bn-simple',
      language: 'bn',
      command: 'set',
      priority: 90,
      template: {
        format: 'সেট {destination} {patient}',
        tokens: [
          { type: 'literal', value: 'সেট', alternatives: ['নির্ধারণ'] },
          { type: 'role', role: 'destination' },
          { type: 'role', role: 'patient' },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { position: 2 },
      },
    },
  ];
}

function getSetPatternsDe(): LanguagePattern[] {
  return [
    {
      id: 'set-de-full',
      language: 'de',
      command: 'set',
      priority: 100,
      template: {
        format: 'setze {destination} auf {patient}',
        tokens: [
          // setzen/stellen are deliberately ABSENT: they are de's PUT verbs (dict
          // put: setzen; profile put: setzen/stellen) — listing them here made
          // every transformed put (`setzen es zu körper`) parse as set.
          { type: 'literal', value: 'setze', alternatives: ['set'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'auf', alternatives: ['zu', 'an'] },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-de-festlegen-auf',
      language: 'de',
      command: 'set',
      priority: 99,
      template: {
        format: 'festlegen auf {destination} {patient}',
        tokens: [
          // setzen deliberately absent — see set-de-full above.
          { type: 'literal', value: 'festlegen', alternatives: ['einstellen'] },
          { type: 'literal', value: 'auf', alternatives: ['an'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 2 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-de-equals',
      language: 'de',
      command: 'set',
      priority: 95,
      template: {
        format: '{destination} = {patient}',
        tokens: [
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: '=' },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 0 },
        patient: { position: 2 },
      },
    },
  ];
}

function getSetPatternsEs(): LanguagePattern[] {
  return [
    {
      id: 'set-es-full',
      language: 'es',
      command: 'set',
      priority: 100,
      template: {
        format: 'establecer {destination} a {patient}',
        tokens: [
          {
            type: 'literal',
            value: 'establecer',
            alternatives: ['fijar', 'definir', 'poner', 'set'],
          },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'a', alternatives: ['en', 'como'] },
          { type: 'role', role: 'patient', expectedTypes: ['literal', 'expression', 'reference'] },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-es-prep-first',
      language: 'es',
      command: 'set',
      priority: 95,
      template: {
        format: 'establecer en {destination} {patient}',
        tokens: [
          {
            type: 'literal',
            value: 'establecer',
            alternatives: ['fijar', 'definir', 'poner', 'set'],
          },
          { type: 'literal', value: 'en', alternatives: ['a'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'role', role: 'patient', expectedTypes: ['literal', 'expression', 'reference'] },
        ],
      },
      extraction: {
        destination: { position: 2 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-es-equals',
      language: 'es',
      command: 'set',
      priority: 95,
      template: {
        format: '{destination} = {patient}',
        tokens: [
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: '=' },
          { type: 'role', role: 'patient', expectedTypes: ['literal', 'expression', 'reference'] },
        ],
      },
      extraction: {
        destination: { position: 0 },
        patient: { position: 2 },
      },
    },
  ];
}

function getSetPatternsFr(): LanguagePattern[] {
  return [
    {
      id: 'set-fr-full',
      language: 'fr',
      command: 'set',
      priority: 100,
      template: {
        format: 'définir {destination} à {patient}',
        tokens: [
          {
            type: 'literal',
            value: 'définir',
            // mettre is deliberately ABSENT: it is fr's PUT verb — listing it here
            // made every transformed put (`mettre ça à corps`) parse as set.
            alternatives: ['definir', 'fixer', 'set'],
          },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'à', alternatives: ['a', 'sur', 'comme'] },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-fr-sur-direct',
      language: 'fr',
      command: 'set',
      priority: 98,
      template: {
        format: 'définir sur {destination} {patient}',
        tokens: [
          { type: 'literal', value: 'définir', alternatives: ['definir', 'mettre', 'fixer'] },
          { type: 'literal', value: 'sur', alternatives: ['à', 'en'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 2 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-fr-equals',
      language: 'fr',
      command: 'set',
      priority: 95,
      template: {
        format: '{destination} = {patient}',
        tokens: [
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: '=' },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 0 },
        patient: { position: 2 },
      },
    },
  ];
}

function getSetPatternsHi(): LanguagePattern[] {
  return [
    // Full pattern: :x को 5 सेट करें
    {
      id: 'set-hi-full',
      language: 'hi',
      command: 'set',
      priority: 100,
      template: {
        format: '{destination} को {patient} सेट करें',
        tokens: [
          { type: 'role', role: 'destination' },
          { type: 'literal', value: 'को' },
          { type: 'role', role: 'patient' },
          { type: 'literal', value: 'सेट', alternatives: ['निर्धारित'] },
          {
            type: 'group',
            optional: true,
            tokens: [{ type: 'literal', value: 'करें', alternatives: ['करो'] }],
          },
        ],
      },
      extraction: {
        destination: { position: 0 },
        patient: { marker: 'को', position: 2 },
      },
    },
    // Simple pattern: सेट :x 5
    {
      id: 'set-hi-simple',
      language: 'hi',
      command: 'set',
      priority: 90,
      template: {
        format: 'सेट {destination} {patient}',
        tokens: [
          { type: 'literal', value: 'सेट', alternatives: ['निर्धारित'] },
          { type: 'role', role: 'destination' },
          { type: 'role', role: 'patient' },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { position: 2 },
      },
    },
  ];
}

function getSetPatternsId(): LanguagePattern[] {
  return [
    {
      id: 'set-id-full',
      language: 'id',
      command: 'set',
      priority: 100,
      template: {
        format: 'atur {destination} ke {patient}',
        tokens: [
          { type: 'literal', value: 'atur', alternatives: ['tetapkan', 'setel', 'set'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'ke', alternatives: ['menjadi', 'jadi', 'pada'] },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-id-pada-direct',
      language: 'id',
      command: 'set',
      priority: 98,
      template: {
        format: 'atur pada {destination} {patient}',
        tokens: [
          { type: 'literal', value: 'atur', alternatives: ['tetapkan', 'setel'] },
          { type: 'literal', value: 'pada', alternatives: ['ke', 'di'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 2 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-id-equals',
      language: 'id',
      command: 'set',
      priority: 95,
      template: {
        format: '{destination} = {patient}',
        tokens: [
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: '=' },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 0 },
        patient: { position: 2 },
      },
    },
  ];
}

function getSetPatternsMs(): LanguagePattern[] {
  return [
    {
      // Malay set mirrors Indonesian: the transformer emits `tetapkan {destination}
      // ke {patient}` for `set X to Y` (`ke` = "to"; the variable being set leads).
      // ms had no set pattern of its own, so the transformed `set` dropped inside
      // event bodies. Canonical roles (destination = the var, patient = the value).
      // See ZH_BLOCK_BODY_SCOPE.md (#2 sweep / ms profile).
      id: 'set-ms-full',
      language: 'ms',
      command: 'set',
      priority: 100,
      template: {
        format: 'tetapkan {destination} ke {patient}',
        tokens: [
          { type: 'literal', value: 'tetapkan', alternatives: ['setkan', 'set'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'ke', alternatives: ['kepada', 'menjadi', 'jadi'] },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { position: 3 },
      },
    },
  ];
}

function getSetPatternsIt(): LanguagePattern[] {
  return [
    {
      id: 'set-it-full',
      language: 'it',
      command: 'set',
      priority: 100,
      template: {
        format: 'impostare {destination} a {patient}',
        tokens: [
          { type: 'literal', value: 'impostare', alternatives: ['imposta', 'set', 'definire'] },
          {
            type: 'role',
            role: 'destination',
            // property-path opts the of-possessive matcher in (mirrors set-es-full;
            // `impostare the *--primary-color di #theme in …` truncated without it)
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          {
            type: 'group',
            optional: true,
            tokens: [
              // 'in' is what the it grammar transformer emits for en 'to'
              { type: 'literal', value: 'a', alternatives: ['su', 'come', 'in'] },
              { type: 'role', role: 'patient' },
            ],
          },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: 'a', markerAlternatives: ['su', 'come', 'in'] },
      },
    },
    {
      id: 'set-it-simple',
      language: 'it',
      command: 'set',
      priority: 90,
      template: {
        format: 'impostare {destination}',
        tokens: [
          { type: 'literal', value: 'impostare', alternatives: ['imposta', 'set'] },
          { type: 'role', role: 'destination' },
        ],
      },
      extraction: {
        destination: { position: 1 },
      },
    },
  ];
}

function getSetPatternsPl(): LanguagePattern[] {
  return [
    {
      id: 'set-pl-full',
      language: 'pl',
      command: 'set',
      priority: 100,
      template: {
        format: 'ustaw {destination} na {patient}',
        tokens: [
          { type: 'literal', value: 'ustaw', alternatives: ['określ', 'okresl', 'przypisz'] },
          {
            type: 'role',
            role: 'destination',
            // property-path opts the of-possessive matcher in (mirrors set-es-full)
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          {
            type: 'group',
            optional: true,
            tokens: [
              { type: 'literal', value: 'na', alternatives: ['do', 'jako'] },
              { type: 'role', role: 'patient' },
            ],
          },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: 'na', markerAlternatives: ['do', 'jako'] },
      },
    },
    {
      id: 'set-pl-simple',
      language: 'pl',
      command: 'set',
      priority: 90,
      template: {
        format: 'ustaw {destination}',
        tokens: [
          { type: 'literal', value: 'ustaw', alternatives: ['określ', 'okresl'] },
          { type: 'role', role: 'destination' },
        ],
      },
      extraction: {
        destination: { position: 1 },
      },
    },
  ];
}

function getSetPatternsPt(): LanguagePattern[] {
  return [
    {
      id: 'set-pt-full',
      language: 'pt',
      command: 'set',
      priority: 100,
      template: {
        format: 'definir {destination} para {patient}',
        tokens: [
          // colocar is deliberately ABSENT: it is pt's PUT verb — listing it here
          // made every transformed put (`colocar isso para corpo`) parse as set.
          { type: 'literal', value: 'definir', alternatives: ['estabelecer', 'set'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'para', alternatives: ['como', 'a', 'em'] },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-pt-em-direct',
      language: 'pt',
      command: 'set',
      priority: 98,
      template: {
        format: 'definir em {destination} {patient}',
        tokens: [
          // colocar deliberately absent — see above.
          { type: 'literal', value: 'definir', alternatives: ['estabelecer'] },
          { type: 'literal', value: 'em', alternatives: ['para', 'a'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 2 },
        patient: { position: 3 },
      },
    },
    {
      id: 'set-pt-equals',
      language: 'pt',
      command: 'set',
      priority: 95,
      template: {
        format: '{destination} = {patient}',
        tokens: [
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: '=' },
          { type: 'role', role: 'patient', expectedTypes: SET_PATIENT_TYPES },
        ],
      },
      extraction: {
        destination: { position: 0 },
        patient: { position: 2 },
      },
    },
  ];
}

function getSetPatternsRu(): LanguagePattern[] {
  return [
    {
      id: 'set-ru-full',
      language: 'ru',
      command: 'set',
      priority: 100,
      template: {
        format: 'установить {destination} в {patient}',
        tokens: [
          { type: 'literal', value: 'установить', alternatives: ['установи', 'задать', 'задай'] },
          {
            type: 'role',
            role: 'destination',
            // property-path opts the of-possessive matcher in (mirrors set-es-full)
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          {
            type: 'group',
            optional: true,
            tokens: [
              { type: 'literal', value: 'в', alternatives: ['на', 'как'] },
              { type: 'role', role: 'patient' },
            ],
          },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: 'в', markerAlternatives: ['на', 'как'] },
      },
    },
    {
      id: 'set-ru-simple',
      language: 'ru',
      command: 'set',
      priority: 90,
      template: {
        format: 'установить {destination}',
        tokens: [
          { type: 'literal', value: 'установить', alternatives: ['установи', 'задать', 'задай'] },
          { type: 'role', role: 'destination' },
        ],
      },
      extraction: {
        destination: { position: 1 },
      },
    },
  ];
}

function getSetPatternsSw(): LanguagePattern[] {
  return [
    {
      // After the i18n dict realign (set → `seti`, was the put verb `weka`), the
      // transformer emits `seti {destination} kwa {patient}` for `set X to Y` — the
      // variable being set leads (unmarked), and `kwa` ("to"/"with") introduces the
      // value. The generated pattern arranged the markers differently, so the
      // transformed `set` dropped. This maps the transform to the canonical set roles
      // (destination = the var, patient = the value). See ZH_BLOCK_BODY_SCOPE.md (#2).
      id: 'set-sw-kwa',
      language: 'sw',
      command: 'set',
      priority: 100,
      template: {
        format: 'seti {destination} kwa {patient}',
        tokens: [
          { type: 'literal', value: 'seti', alternatives: ['weka thamani'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'kwa', alternatives: ['kwenye'] },
          {
            type: 'role',
            role: 'patient',
            expectedTypes: SET_PATIENT_TYPES,
          },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: 'kwa', markerAlternatives: ['kwenye'] },
      },
    },
  ];
}

function getSetPatternsTh(): LanguagePattern[] {
  return [
    // Marker pattern: ตั้ง X ใน "val" — every th corpus emission marks the
    // value with ใน (en 'to'); positional patient mis-captured multi-token
    // destinations (property paths) and fabricated bogus values.
    {
      id: 'set-th-simple',
      language: 'th',
      command: 'set',
      priority: 100,
      template: {
        format: 'ตั้ง {destination} ใน {patient}',
        tokens: [
          { type: 'literal', value: 'ตั้ง', alternatives: ['กำหนด', 'ตั้งค่า'] },
          {
            type: 'role',
            role: 'destination',
            // property-path opts the of-possessive matcher in (mirrors set-es-full)
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          {
            type: 'group',
            optional: true,
            tokens: [
              { type: 'literal', value: 'ใน', alternatives: ['เป็น'] },
              { type: 'role', role: 'patient' },
            ],
          },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: 'ใน', markerAlternatives: ['เป็น'] },
      },
    },
  ];
}

function getSetPatternsUk(): LanguagePattern[] {
  return [
    {
      id: 'set-uk-full',
      language: 'uk',
      command: 'set',
      priority: 100,
      template: {
        format: 'встановити {destination} в {patient}',
        tokens: [
          { type: 'literal', value: 'встановити', alternatives: ['встанови', 'задати', 'задай'] },
          {
            type: 'role',
            role: 'destination',
            // property-path opts the of-possessive matcher in (mirrors set-es-full)
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          {
            type: 'group',
            optional: true,
            tokens: [
              { type: 'literal', value: 'в', alternatives: ['на', 'як'] },
              { type: 'role', role: 'patient' },
            ],
          },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: 'в', markerAlternatives: ['на', 'як'] },
      },
    },
    {
      id: 'set-uk-simple',
      language: 'uk',
      command: 'set',
      priority: 90,
      template: {
        format: 'встановити {destination}',
        tokens: [
          { type: 'literal', value: 'встановити', alternatives: ['встанови', 'задати', 'задай'] },
          { type: 'role', role: 'destination' },
        ],
      },
      extraction: {
        destination: { position: 1 },
      },
    },
  ];
}

function getSetPatternsHe(): LanguagePattern[] {
  return [
    {
      // Hebrew set. The i18n grammar transformer emits the accusative-fronted form
      // `קבע את {destination} על {patient}` for `set X to Y` — `את` is the direct-
      // object marker on the variable being set, and `על` ("on"/"to") introduces
      // the value. The generated pattern used the bare profile markers (destination
      // → על, patient → את) in the opposite arrangement, so the transformed form
      // didn't match and the `set` dropped (degenerate `template-literal-list-build`
      // in he). `את` is required: the transformer always fronts the accusative for
      // set, and keeping it required (rather than an optional group) avoids the
      // matcher producing an empty partial match on unrelated `…את…` inputs.
      // See docs-internal/ZH_BLOCK_BODY_SCOPE.md (#2 sweep — per-language set marking).
      id: 'set-he-full',
      language: 'he',
      command: 'set',
      priority: 100,
      template: {
        format: 'קבע את {destination} על {patient}',
        tokens: [
          { type: 'literal', value: 'קבע', alternatives: ['הגדר'] },
          { type: 'literal', value: 'את' },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'על', alternatives: ['ב', 'אל', 'ל', 'כ'] },
          {
            type: 'role',
            role: 'patient',
            expectedTypes: SET_PATIENT_TYPES,
          },
        ],
      },
      extraction: {
        destination: { marker: 'את' },
        patient: { marker: 'על', markerAlternatives: ['ב', 'אל', 'ל', 'כ'] },
      },
    },
  ];
}

function getSetPatternsVi(): LanguagePattern[] {
  return [
    {
      // The i18n grammar transformer emits `gán {destination} vào {patient}` for
      // `set X to Y` — `vào` is vi's destination/"into" marker, and the variable
      // being set leads (unmarked). The existing `set-vi-full` below uses a
      // different marker (`thành`) and non-canonical roles (var → `patient`), so it
      // never matched the transform output and the `set` dropped (degenerate vi in
      // template-literal-list-build etc.). This pattern matches the transform and
      // assigns the canonical set roles (destination = the var, patient = the value)
      // that the AST mapper reads. See ZH_BLOCK_BODY_SCOPE.md (#2 sweep).
      id: 'set-vi-vao',
      language: 'vi',
      command: 'set',
      priority: 101,
      template: {
        format: 'gán {destination} vào {patient}',
        tokens: [
          { type: 'literal', value: 'gán', alternatives: ['thiết lập', 'đặt giá trị'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'vào', alternatives: ['cho', 'đến'] },
          {
            type: 'role',
            role: 'patient',
            expectedTypes: ['literal', 'expression', 'reference', 'selector'],
          },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: 'vào', markerAlternatives: ['cho', 'đến'] },
      },
    },
    {
      id: 'set-vi-full',
      language: 'vi',
      command: 'set',
      priority: 100,
      template: {
        format: 'gán {destination} thành {patient}',
        tokens: [
          { type: 'literal', value: 'gán', alternatives: ['thiết lập', 'đặt giá trị'] },
          { type: 'role', role: 'patient' },
          { type: 'literal', value: 'thành', alternatives: ['bằng', 'là'] },
          { type: 'role', role: 'goal' },
        ],
      },
      extraction: {
        patient: { position: 1 },
        goal: { marker: 'thành', markerAlternatives: ['bằng', 'là'] },
      },
    },
    {
      id: 'set-vi-simple',
      language: 'vi',
      command: 'set',
      priority: 90,
      template: {
        format: 'đặt {destination} là {patient}',
        tokens: [
          { type: 'literal', value: 'đặt' },
          { type: 'role', role: 'patient' },
          { type: 'literal', value: 'là' },
          { type: 'role', role: 'goal' },
        ],
      },
      extraction: {
        patient: { position: 1 },
        goal: { marker: 'là' },
      },
    },
  ];
}

function getSetPatternsZh(): LanguagePattern[] {
  return [
    {
      id: 'set-zh-full',
      language: 'zh',
      command: 'set',
      priority: 100,
      template: {
        // RENDERS 到, not the natural 为. `为` IS zh's `for` keyword — the
        // tokenizer normalizes it to `for`, a block OPENER — so a rendered
        // `设置 X 为 V` made the block parser's depth counter treat every set as
        // opening a nested block. A behavior with TWO of them never balanced:
        // parseBehaviorBlock produced no segment and the whole behavior/on
        // structure collapsed to a bare command chain (draggable, resizable,
        // sortable in zh). ONE set was fine, which is why nothing smaller caught
        // it. The schema already named the unambiguous form —
        // setSchema.patient.markerOverride.zh is 到, with 为/為/成 as
        // markerVariants — so this pattern was the outlier, emitting the
        // ambiguous member of its own alternatives list. 为 still PARSES.
        format: '设置 {destination} 到 {patient}',
        tokens: [
          { type: 'literal', value: '设置', alternatives: ['設置', '设定', '設定'] },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: '到', alternatives: ['为', '為', '成'] },
          { type: 'role', role: 'patient', expectedTypes: ['literal', 'expression', 'reference'] },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: '到', markerAlternatives: ['为', '為', '成'] },
      },
    },
    {
      // BA-fronted set. The i18n grammar transformer emits the *split* verb form
      // `把 {destination} 设置 到 {patient}` for `set X to Y` (verb 设置 + a
      // separate "to"-marker 到), so the marker is matched as an optional group —
      // when absent, the merged verb forms (设置为/设定为) carry the "as"
      // themselves. Mirrors the put-zh-ba fix. See ZH_BLOCK_BODY_SCOPE.md (#3/#2).
      id: 'set-zh-ba',
      language: 'zh',
      command: 'set',
      priority: 95,
      template: {
        format: '把 {destination} 设置 到 {patient}',
        tokens: [
          { type: 'literal', value: '把' },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          {
            type: 'literal',
            value: '设置',
            alternatives: ['設置', '设定', '設定', '设置为', '設置為', '设定为', '設定為'],
          },
          {
            type: 'group',
            optional: true,
            tokens: [{ type: 'literal', value: '到', alternatives: ['为', '為', '成'] }],
          },
          { type: 'role', role: 'patient', expectedTypes: ['literal', 'expression', 'reference'] },
        ],
      },
      extraction: {
        destination: { position: 1 },
        patient: { marker: '到', markerAlternatives: ['为', '為', '成'] },
      },
    },
    {
      // Verb-first BA: 设置 把 {destination} 到 {patient}. For an `on click set
      // X to Y` handler the i18n transformer fronts the verb and marks the
      // leading argument with 把 (its generic argument parser defaults the
      // leading argument to the patient role; zh marks patient with 把) —
      // `当 点击 时 设置 把 @disabled 到 真`. Neither set-zh-full (no 把) nor
      // set-zh-ba (把 BEFORE the verb) matches, so the whole set body dropped
      // across the set-* corpus patterns. Same BA-tolerance family as
      // wait-zh-ba / put-zh-ba. See ZH_BLOCK_BODY_SCOPE.md.
      id: 'set-zh-vba',
      language: 'zh',
      command: 'set',
      priority: 96,
      template: {
        format: '设置 把 {destination} 到 {patient}',
        tokens: [
          {
            type: 'literal',
            value: '设置',
            alternatives: ['設置', '设定', '設定'],
          },
          { type: 'literal', value: '把' },
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          {
            type: 'group',
            optional: true,
            tokens: [{ type: 'literal', value: '到', alternatives: ['为', '為', '成'] }],
          },
          { type: 'role', role: 'patient', expectedTypes: ['literal', 'expression', 'reference'] },
        ],
      },
      extraction: {
        destination: { position: 2 },
        patient: { marker: '到', markerAlternatives: ['为', '為', '成'] },
      },
    },
    {
      id: 'set-zh-simple',
      language: 'zh',
      command: 'set',
      priority: 90,
      template: {
        format: '{destination} = {patient}',
        tokens: [
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: '=' },
          { type: 'role', role: 'patient', expectedTypes: ['literal', 'expression', 'reference'] },
        ],
      },
      extraction: {
        destination: { position: 0 },
        patient: { position: 2 },
      },
    },
  ];
}

/**
 * Get set patterns for a specific language.
 */
/**
 * Quechua (SOV) set with an interleaved oblique source phrase.
 *
 * The transformer renders `on input from #firstName set <dest> to <value>`
 * with the handler's from-phrase INSIDE the body clause, between the
 * ta-marked destination and the man-marked patient:
 *   `#greeting.innerText ta #firstName manta "Hello, " + noqaq chanin man
 *    yaykuchiy pi churanay`                (two-way-binding, computed-value)
 * The generated `set-qu-generated` has no slot for `<source> manta`, so the
 * whole clause fell to the verb-anchoring fallback, which can type neither
 * the property-path destination nor the operator-run patient (destination
 * came back `literal`, patient `selector`/`literal` — the R1 Family B
 * cluster; en: destination:property-path + patient:expression via
 * set-en-possessive).
 *
 * The manta group is REQUIRED here: the plain `{destination} ta {patient}
 * man churanay` shape already parses correctly through the generated
 * pattern, so this one fires only on the oblique-source shape and shadows
 * nothing. The source lands on the node exactly as the fallback used to
 * emit it (schema-extra, invisible to every signal). `churay` is
 * deliberately absent from the verb list — it is qu's PUT verb (the de
 * setzen-collision precedent).
 */
function getSetPatternsQu(): LanguagePattern[] {
  return [
    {
      id: 'set-qu-oblique-source',
      language: 'qu',
      command: 'set',
      priority: 105,
      template: {
        format: '{destination} ta {source} manta {patient} man churanay',
        tokens: [
          {
            type: 'role',
            role: 'destination',
            expectedTypes: ['property-path', 'selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'ta' },
          {
            type: 'role',
            role: 'source',
            expectedTypes: ['selector', 'reference', 'expression'],
          },
          { type: 'literal', value: 'manta' },
          { type: 'role', role: 'patient', expectedTypes: ['literal', 'expression', 'reference'] },
          { type: 'literal', value: 'man' },
          { type: 'literal', value: 'churanay', alternatives: ['kamaykuy'] },
        ],
      },
      extraction: {
        destination: { position: 0 },
        source: { position: 2 },
        patient: { position: 4 },
      },
    },
  ];
}

/**
 * Append an optional trailing `[on {scope}]` group to each hand-crafted set
 * pattern (S1 tabs-aria). These patterns tie the generated `set-*-generated`
 * pattern on priority and win on stable-sort order, so without the scope group
 * they silently shadow it and drop `on <scope>`. The i18n transformer appends
 * `on <scope>` at the clause end in every word order (transformSetWithScope),
 * which a trailing group matches for verb-first (SVO/VSO) orders; SOV is handled
 * by the dedicated event-handler scope patterns. Idempotent: skips a pattern
 * that already declares a scope role. See setSchema's scope role.
 */
function withTrailingScope(patterns: LanguagePattern[]): LanguagePattern[] {
  return patterns.map(p => {
    if (p.extraction?.scope) return p;
    return {
      ...p,
      template: {
        ...p.template,
        tokens: [
          ...p.template.tokens,
          {
            type: 'group',
            optional: true,
            tokens: [
              { type: 'literal', value: 'on' },
              {
                type: 'role',
                role: 'scope',
                optional: true,
                expectedTypes: ['selector', 'reference'],
              },
            ],
          },
        ],
      },
      extraction: { ...p.extraction, scope: { marker: 'on' } },
    };
  });
}

export function getSetPatternsForLanguage(language: string): LanguagePattern[] {
  switch (language) {
    case 'bn':
      return withTrailingScope(getSetPatternsBn());
    case 'de':
      return withTrailingScope(getSetPatternsDe());
    case 'es':
      return withTrailingScope(getSetPatternsEs());
    case 'fr':
      return withTrailingScope(getSetPatternsFr());
    case 'he':
      return withTrailingScope(getSetPatternsHe());
    case 'hi':
      return withTrailingScope(getSetPatternsHi());
    case 'id':
      return withTrailingScope(getSetPatternsId());
    case 'ms':
      return withTrailingScope(getSetPatternsMs());
    case 'it':
      return withTrailingScope(getSetPatternsIt());
    case 'pl':
      return withTrailingScope(getSetPatternsPl());
    case 'pt':
      return withTrailingScope(getSetPatternsPt());
    case 'qu':
      return withTrailingScope(getSetPatternsQu());
    case 'ru':
      return withTrailingScope(getSetPatternsRu());
    case 'sw':
      return withTrailingScope(getSetPatternsSw());
    case 'th':
      return withTrailingScope(getSetPatternsTh());
    case 'uk':
      return withTrailingScope(getSetPatternsUk());
    case 'vi':
      return withTrailingScope(getSetPatternsVi());
    case 'zh':
      return withTrailingScope(getSetPatternsZh());
    default:
      return [];
  }
}
